/**
 * Allgemeine Utility-Methoden im Zusammenhang mit base.metamodel.json / Model,
 * welche für 3D-Modelle benutzt werden
 */
import {v3} from "oc-threejs/short3";
import Scenes from "oc-threejs/Scenes";
import Urls from "oc-commons/Urls";
import UniversalLoader from "oc-threejs/loader/UniversalLoader";
import ReplacingMaterialFactory from "oc-threejs/loader/ReplacingMaterialFactory";
import Commons from "oc-commons/Commons";
import Connector from "oc-interaction/connectors/Connector";
import Textures from "oc-threejs/Textures";
import CoordinateSystem from "oc-threejs/math/CoordinateSystem";

export default class ModelsHelper {

    /**
     * @param model ein Eintrag der Model - Tabelle (also des Metamodels)
     * @param position out-parameter für position
     * @param quaternion out-parameter für quaternion
     * @param scale out-parameter für scale
     */
    applyToCoordinateSystem(model, position, quaternion, scale) {
        if (model.Rotation) {
            quaternion.copy(model.Rotation);
        } else {
            quaternion.set(0, 0, 0, 1);
        }

        if (model.Scale) {
            scale.copy(model.Scale);
        } else {
            scale.set(1, 1, 1);
        }

        position.set(0, 0, 0);

        if (model.AutoCenter) {
            const gridPosition = model.GridPosition || v3(0.5, 0.5, 0.5);
            const boundingBox =
                model.ModelData.boundingBoxUntransformed.clone()
                    .applyMatrix4(
                new THREE.Matrix4().compose ( position, quaternion, scale ));
            const c = boundingBox.grid(gridPosition).negate();
            position.add(c);
        }

        if (model.Position) {
            position.add(model.Position);
        }
    }

    /**
     * Gibt die boundingBox basierend auf der transformierten ModelData.boundingBoxUntransformed zurück.
     *
     * Früher wurde in ModelData sowohl boundingBox als auch boundingBoxUntransformed gespeichert.
     * Problem daran war, dass die boundingBox sich durch Änderungen an position, quaternion oder scale
     * ändert, und aktualisiert werden muss. Das musste aber manuell erfolgen, weil ein garantiertes automatisches
     * Tracken der Änderung im Backend und neuberechnen der boundingbox sehr aufwändig gewesen wäre.
     * Daher ist der neue Ansatz, auf das persistieren der boundingBox zu verzichten, und nur noch die
     * boundingBoxUntransformed zu persisiteren. Die boundingBox wird über den ModelsHelper zur Laufzeit
     * aus der boundingBoxUntransformed berechnet.
     *
     * @param model
     */
    getBoundingBox(model) {
        if (model?.ModelData==null) {
            return new THREE.Box3();
        }
        const coordinateSystem = new CoordinateSystem();
        this.applyToCoordinateSystem(model, coordinateSystem.position, coordinateSystem.quaternion, coordinateSystem.scale);
        const matrix = coordinateSystem.getMatrix();
        return model.ModelData.boundingBoxUntransformed.clone().applyMatrix4(matrix);
    }

    /**
     * Wendet die Model-Paramter Rotation, Scale, AutoCenter und Position auf ein 3D-Objekt an
     */
    applyToObject3d(model, object3d) {
        this.applyToCoordinateSystem(model, object3d.position, object3d.quaternion, object3d.scale);
        object3d.updateMatrix();
        if (STAGE.settings.shadows.enabled) {
            Scenes.filter(object3d, o => o instanceof THREE.Mesh)
                .forEach(o => {
                    o.receiveShadow = true;
                    o.castShadow = true;
                });
        }
        return object3d;
    }

    getConnectorsFromModelRelativeToObject(model, object3d){
        const connectors=[];
        if (model.Connector && Array.isArray(model.Connector)) {
            model.Connector.forEach(config => {
                const connector = new Connector([config.connectorname, config.connectortype, config.strengthFactor]);
                const connectorOptions = {
                    position: v3(object3d.posbox.grid(config.position_relativ)).add(config.position_absolut),
                    quaternion: config.rotation
                };
                connectors.push(connector);
                connector.setConnectorOptions(connectorOptions);
            });
        }
        return connectors;
    }

    /**
     * @returns {Promise}
     */
    loadModelPromise(model,materialMap=undefined) {
        const url = Urls.isAbsolute(model.Path) ? model.Path : APP.settings.wkfgContentBaseUrl + model.Path;
        const map = Object.assign({},materialMap,model.MaterialMap);
        const materialFactory = new ReplacingMaterialFactory(map, UniversalLoader.getMaterialFactory());
        const cacheKey = Commons.cacheKey(model.Path, map);
        return UniversalLoader.loadModelPromise(url, materialFactory, cacheKey)
            .then(object => {
                return this.applyToObject3d(model, object)
            })
    }

    setModelDataFromObject(model, object3d) {
        if (!model.ModelData) {
            model.ModelData={};
        }
        model.ModelData.materialNames = Textures.getMaterialNames(object3d);
        model.ModelData.objectNames = _.uniq(Scenes.map(object3d, o => o.name)).filter(x=>!Commons.isEmpty(x));
        const o2 = object3d.clone();
        o2.position.set(0,0,0);
        o2.quaternion.set(0,0,0,1);
        o2.scale.set(1,1,1);
        model.ModelData.boundingBoxUntransformed = Scenes.boundingBoxLocalMatrix(o2);
        this.applyToCoordinateSystem(model, o2.position, o2.quaternion, o2.scale);
    }

}
